/*
 * FabFiltersBar — the SEED FAB page's filters (Product Insights overhaul
 * 2026-10-19). Rendered INSIDE the page's `PiToolbar`, so it draws no panel or
 * border of its own: one wrapping row of controls, then the active-filter
 * chips (`PiFilterChips`, with Reset) on a full-width line below.
 *
 * Layout:
 * - md and up: Cycle · Region · Source · First product · Search (only when
 *   `showSearch`) · "More filters (n)", n = advanced filters that are set.
 * - Below md: Cycle (full width) · Search · "Filters (n)", n = every filter
 *   that is set. Both buttons open the SAME Modal; on small screens it also
 *   carries the primary filters (they are `md:hidden` inside it).
 *
 * Controlled: `value` is the page's FabFilterState (URL-backed) and every
 * change goes out whole through `onChange`. The page owns the search debounce
 * (typed text reaches the tables at once, the URL after 250 ms), so the
 * search box emits on every keystroke.
 *
 * Rules that live only here:
 * - The cycle is SEED FAB's own (Jun–May), never an EMERGE cycle. The resolved
 *   (current) cycle is the default: choosing it writes `cycleId: null`.
 * - Reset keeps the cycle and the Schools stage (the stage is the Schools
 *   tab's own toggle); the cycle has no chip because the select always shows it.
 * - Regions and countries are mutually exclusive: upstream, `country_ids`
 *   REPLACES `region_ids`, so choosing one side clears the other.
 * - Intakes are only meaningful with the intake split on; turning it off
 *   clears them.
 * - Round / evaluation / submission are scholarship-only; they show (in the
 *   modal and as chips) only when `showScholarshipFilters`.
 */
import { useState } from 'react';
import {
  Button,
  Checkbox,
  CountBadge,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Select,
} from '@/components/ui';
import type { MultiComboboxOption } from '@/components/ui';
import { formatDateOnly } from '@/lib/format';
import type { FabCountry, FabFilters } from '@/lib/types';
import {
  DEFAULT_EVALUATION_STATUS,
  DEFAULT_SUBMISSION_STATUS,
  type FabFilterState,
} from './fab-filter-state';
import { PiFilterChips, PiSearch } from './pi-kit';

export interface FabFiltersBarProps {
  filters: FabFilters | null;
  countries: FabCountry[];
  value: FabFilterState;
  onChange: (next: FabFilterState) => void;
  showSearch: boolean;
  showScholarshipFilters: boolean;
}

const EVALUATION_OPTIONS = [
  { value: 'all', label: 'Any evaluation' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'junk', label: 'Junk' },
  { value: 'recommended', label: 'Recommended' },
] as const;

const SUBMISSION_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'all', label: 'Submitted or in progress' },
] as const;

const DEFAULT_ROUNDS = [1, 2, 3, 4].map((round) => ({ round, label: `Round ${round}` }));

function toIds(values: string[]): number[] {
  return values.map((v) => Number.parseInt(v, 10)).filter((n) => Number.isInteger(n));
}

function labelOf(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

/** Advanced filters (inside "More filters") that differ from the defaults. */
function advancedCount(value: FabFilterState, scholarship: boolean): number {
  let n = 0;
  if (value.countryIds.length > 0) n += 1;
  if (value.since) n += 1;
  if (value.until) n += 1;
  if (value.intakeSplit) n += 1;
  if (scholarship && value.round !== null) n += 1;
  if (scholarship && value.evaluationStatus !== DEFAULT_EVALUATION_STATUS) n += 1;
  if (scholarship && value.submissionStatus !== DEFAULT_SUBMISSION_STATUS) n += 1;
  return n;
}

/** Primary-row filters that are set (cycle and search excluded: always visible). */
function primaryCount(value: FabFilterState): number {
  let n = 0;
  if (value.regionIds.length > 0) n += 1;
  if (value.sources.length > 0) n += 1;
  if (value.products.length > 0) n += 1;
  return n;
}

export function FabFiltersBar({
  filters,
  countries,
  value,
  onChange,
  showSearch,
  showScholarshipFilters,
}: FabFiltersBarProps) {
  const [open, setOpen] = useState(false);

  // --- derived options -----------------------------------------------------
  const loading = filters === null;
  const cycles = filters?.cycles ?? [];
  const regions = filters?.regions ?? [];
  const sources = filters?.sources ?? [];
  const products = filters?.products ?? [];
  const seasons = filters?.seasons ?? [];
  const rounds = filters?.rounds && filters.rounds.length > 0 ? filters.rounds : DEFAULT_ROUNDS;
  const selectedCycleId = value.cycleId ?? filters?.resolvedCycleId ?? null;

  const regionNameById = new Map(regions.map((r) => [r.id, r.name]));
  const countryNameById = new Map(countries.map((c) => [c.id, c.name]));
  const seasonNameById = new Map(seasons.map((s) => [s.id, s.name]));

  const regionOptions: MultiComboboxOption[] = regions.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));
  const countryOptions: MultiComboboxOption[] = countries.map((c) => {
    const regionName = c.regionId === null ? undefined : regionNameById.get(c.regionId);
    return regionName
      ? { value: String(c.id), label: c.name, hint: regionName }
      : { value: String(c.id), label: c.name };
  });
  const sourceOptions: MultiComboboxOption[] = sources.map((s) => ({
    value: s.value,
    label: s.label,
  }));
  const productOptions: MultiComboboxOption[] = products.map((p) => ({
    value: p.value,
    label: p.label,
  }));
  const seasonOptions: MultiComboboxOption[] = seasons.map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  const set = (patch: Partial<FabFilterState>) => onChange({ ...value, ...patch });

  const setCycle = (raw: string) => {
    const id = Number.parseInt(raw, 10);
    if (!Number.isSafeInteger(id)) return;
    set({ cycleId: filters && id === filters.resolvedCycleId ? null : id });
  };
  const setRegions = (values: string[]) => {
    const regionIds = toIds(values);
    set(regionIds.length > 0 ? { regionIds, countryIds: [] } : { regionIds });
  };
  const setCountries = (values: string[]) => {
    const countryIds = toIds(values);
    set(countryIds.length > 0 ? { countryIds, regionIds: [] } : { countryIds });
  };

  const reset = () => {
    onChange({
      ...value,
      regionIds: [],
      countryIds: [],
      sources: [],
      products: [],
      since: null,
      until: null,
      intakeSplit: false,
      seasonIds: [],
      round: null,
      evaluationStatus: DEFAULT_EVALUATION_STATUS,
      submissionStatus: DEFAULT_SUBMISSION_STATUS,
      search: '',
    });
  };

  const moreCount = advancedCount(value, showScholarshipFilters);
  const mobileCount = moreCount + primaryCount(value);

  // --- active-filter chips -------------------------------------------------
  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  for (const id of value.regionIds) {
    chips.push({
      key: `region-${id}`,
      label: regionNameById.get(id) ?? `Region ${id}`,
      onRemove: () => set({ regionIds: value.regionIds.filter((r) => r !== id) }),
    });
  }
  for (const id of value.countryIds) {
    chips.push({
      key: `country-${id}`,
      label: countryNameById.get(id) ?? `Country ${id}`,
      onRemove: () => set({ countryIds: value.countryIds.filter((c) => c !== id) }),
    });
  }
  for (const source of value.sources) {
    chips.push({
      key: `source-${source}`,
      label: labelOf(sources, source),
      onRemove: () => set({ sources: value.sources.filter((s) => s !== source) }),
    });
  }
  for (const product of value.products) {
    chips.push({
      key: `product-${product}`,
      label: `First product: ${labelOf(products, product)}`,
      onRemove: () => set({ products: value.products.filter((p) => p !== product) }),
    });
  }
  if (value.since) {
    chips.push({
      key: 'since',
      label: `From ${formatDateOnly(value.since)}`,
      onRemove: () => set({ since: null }),
    });
  }
  if (value.until) {
    chips.push({
      key: 'until',
      label: `To ${formatDateOnly(value.until)}`,
      onRemove: () => set({ until: null }),
    });
  }
  if (value.intakeSplit) {
    chips.push({
      key: 'intake-split',
      label: 'Split by intake',
      onRemove: () => set({ intakeSplit: false, seasonIds: [] }),
    });
  }
  for (const id of value.seasonIds) {
    chips.push({
      key: `season-${id}`,
      label: `Intake: ${seasonNameById.get(id) ?? id}`,
      onRemove: () => set({ seasonIds: value.seasonIds.filter((s) => s !== id) }),
    });
  }
  if (showScholarshipFilters) {
    if (value.round !== null) {
      const round = value.round;
      chips.push({
        key: 'round',
        label: rounds.find((r) => r.round === round)?.label ?? `Round ${round}`,
        onRemove: () => set({ round: null }),
      });
    }
    if (value.evaluationStatus !== DEFAULT_EVALUATION_STATUS) {
      chips.push({
        key: 'evaluation',
        label: `Evaluation: ${labelOf(EVALUATION_OPTIONS, value.evaluationStatus)}`,
        onRemove: () => set({ evaluationStatus: DEFAULT_EVALUATION_STATUS }),
      });
    }
    if (value.submissionStatus !== DEFAULT_SUBMISSION_STATUS) {
      chips.push({
        key: 'submission',
        label: labelOf(SUBMISSION_OPTIONS, value.submissionStatus),
        onRemove: () => set({ submissionStatus: DEFAULT_SUBMISSION_STATUS }),
      });
    }
  }
  if (showSearch && value.search.trim()) {
    chips.push({
      key: 'search',
      label: `“${value.search.trim()}”`,
      onRemove: () => set({ search: '' }),
    });
  }

  // --- pieces --------------------------------------------------------------
  const cycleControl = (
    <Select
      aria-label="Cycle"
      value={selectedCycleId !== null ? String(selectedCycleId) : ''}
      onChange={(e) => setCycle(e.target.value)}
      disabled={loading}
    >
      {loading && <option value="">Cycle</option>}
      {cycles.map((c) => (
        <option key={c.id} value={String(c.id)}>
          {c.isCurrent ? `${c.name} (current)` : c.name}
        </option>
      ))}
    </Select>
  );

  const regionCombobox = (id?: string) => (
    <MultiCombobox
      id={id}
      values={value.regionIds.map(String)}
      options={regionOptions}
      loading={loading}
      placeholder="All regions"
      summaryNoun={{ singular: 'region', plural: 'regions' }}
      onChange={setRegions}
    />
  );

  const sourceCombobox = (id?: string) => (
    <MultiCombobox
      id={id}
      values={value.sources}
      options={sourceOptions}
      loading={loading}
      placeholder="All sources"
      summaryNoun={{ singular: 'source', plural: 'sources' }}
      onChange={(next) => set({ sources: next })}
    />
  );

  const productCombobox = (id?: string) => (
    <MultiCombobox
      id={id}
      values={value.products}
      options={productOptions}
      loading={loading}
      placeholder="All first products"
      summaryNoun={{ singular: 'product', plural: 'products' }}
      onChange={(next) => set({ products: next })}
    />
  );

  const searchControl = (
    <PiSearch
      value={value.search}
      onChange={(next) => set({ search: next })}
      label={showScholarshipFilters ? 'Search scholarships' : 'Search schools'}
    />
  );

  const filtersButton = (count: number, text: string, className?: string) => (
    <Button
      variant="secondary"
      className={className}
      aria-label={count > 0 ? `${text}, ${count} set` : text}
      onClick={() => setOpen(true)}
    >
      <span className="inline-flex items-center gap-1.5">
        {text}
        <CountBadge count={count} />
      </span>
    </Button>
  );

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <div className="w-48 max-md:w-full">{cycleControl}</div>

      {/* md and up: the primary filters inline. */}
      <div className="hidden w-44 md:block">
        <Field label="Region" hideLabel>
          {(field) => regionCombobox(field.id)}
        </Field>
      </div>
      <div className="hidden w-40 md:block">
        <Field label="Source" hideLabel>
          {(field) => sourceCombobox(field.id)}
        </Field>
      </div>
      <div className="hidden w-48 md:block">
        <Field label="First product" hideLabel>
          {(field) => productCombobox(field.id)}
        </Field>
      </div>

      {showSearch && <div className="min-w-0 flex-1 md:max-w-64 md:min-w-48">{searchControl}</div>}

      <div className="hidden md:block">{filtersButton(moreCount, 'More filters')}</div>
      <div className={showSearch ? 'md:hidden' : 'flex-1 md:hidden'}>
        {filtersButton(mobileCount, 'Filters', showSearch ? undefined : 'w-full')}
      </div>

      {chips.length > 0 && (
        <div className="min-w-0 basis-full">
          <PiFilterChips chips={chips} onReset={reset} />
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Filters"
        size="md"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button variant="ghost" onClick={reset} disabled={chips.length === 0}>
              Reset
            </Button>
            <Button variant="primary" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Primary filters repeat here only on small screens. */}
          <div className="md:hidden">
            <Field label="Region">{(field) => regionCombobox(field.id)}</Field>
          </div>
          <div className="md:hidden">
            <Field label="Source">{(field) => sourceCombobox(field.id)}</Field>
          </div>
          <div className="sm:col-span-2 md:hidden">
            <Field label="First product">{(field) => productCombobox(field.id)}</Field>
          </div>

          <div className="sm:col-span-2">
            <Field label="Countries">
              {(field) => (
                <MultiCombobox
                  id={field.id}
                  values={value.countryIds.map(String)}
                  options={countryOptions}
                  placeholder="All countries"
                  summaryNoun={{ singular: 'country', plural: 'countries' }}
                  onChange={setCountries}
                />
              )}
            </Field>
          </div>

          <Field label="From">
            {(field) => (
              <Input
                {...field}
                type="date"
                value={value.since ?? ''}
                max={value.until ?? undefined}
                onChange={(e) => set({ since: e.target.value || null })}
              />
            )}
          </Field>
          <Field label="To">
            {(field) => (
              <Input
                {...field}
                type="date"
                value={value.until ?? ''}
                min={value.since ?? undefined}
                onChange={(e) => set({ until: e.target.value || null })}
              />
            )}
          </Field>

          <div className="flex h-9 items-center self-end max-sm:h-11">
            <Checkbox
              label="Split by intake"
              checked={value.intakeSplit}
              onChange={(e) =>
                set(e.target.checked ? { intakeSplit: true } : { intakeSplit: false, seasonIds: [] })
              }
            />
          </div>
          <Field label="Intakes">
            {(field) => (
              <MultiCombobox
                id={field.id}
                values={value.seasonIds.map(String)}
                options={seasonOptions}
                loading={loading}
                disabled={!value.intakeSplit}
                placeholder="All intakes"
                summaryNoun={{ singular: 'intake', plural: 'intakes' }}
                onChange={(next) => set({ seasonIds: toIds(next) })}
              />
            )}
          </Field>

          {showScholarshipFilters && (
            <>
              <Field label="Scholarship round">
                {(field) => (
                  <Select
                    {...field}
                    value={value.round === null ? '' : String(value.round)}
                    onChange={(e) => {
                      const round = Number.parseInt(e.target.value, 10);
                      set({ round: Number.isInteger(round) ? round : null });
                    }}
                  >
                    <option value="">Any round</option>
                    {rounds.map((r) => (
                      <option key={r.round} value={String(r.round)}>
                        {r.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Evaluation">
                {(field) => (
                  <Select
                    {...field}
                    value={value.evaluationStatus}
                    onChange={(e) => set({ evaluationStatus: e.target.value })}
                  >
                    {EVALUATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Application status">
                {(field) => (
                  <Select
                    {...field}
                    value={value.submissionStatus}
                    onChange={(e) => set({ submissionStatus: e.target.value })}
                  >
                    {SUBMISSION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
